import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { mailApi, categoryApi, mailAccountApi, attachmentApi, myCustomerApi } from '../api/client';
import useAuthStore from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfidenceBadge from '../components/ConfidenceBadge';
import SafeHtmlMail from '../components/SafeHtmlMail';
import ComposeModal from './ComposeModal';

const MIN_PANEL_WIDTH = 280;
const MAX_PANEL_WIDTH = 700;
const DEFAULT_PANEL_WIDTH = 384; // w-96

export default function PortalMails() {
  const { user } = useAuthStore();
  const location = useLocation();
  const isSpamRoute = location.pathname.startsWith('/portal/spam');
  const [mails, setMails] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filters, setFilters] = useState({ search: '', category: '', mail_account: '', is_read: '', is_spam: isSpamRoute ? 'true' : '', date_from: '', date_to: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [compose, setCompose] = useState(null);   // null=zu | false=neu | MailMessage=antworten
  const [forward, setForward] = useState(null);   // null=zu | MailMessage=weiterleiten
  const [fetching, setFetching] = useState(false);
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('mail_panel_width');
    return saved ? Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, parseInt(saved, 10))) : DEFAULT_PANEL_WIDTH;
  });
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 640);
  const [htmlMailDisplay, setHtmlMailDisplay] = useState('ask'); // Customer-Einstellung: text | ask | html
  const [contentView, setContentView] = useState('plain'); // aktuelle Anzeige der geöffneten Mail: plain | html

  // Kundeneinstellung für HTML-Mail-Anzeige laden
  useEffect(() => {
    myCustomerApi.get().then(({ data }) => {
      if (data?.html_mail_display) setHtmlMailDisplay(data.html_mail_display);
    }).catch(() => {});
  }, []);

  // Bei jeder neu geöffneten Mail die Standardansicht gemäß Kundeneinstellung setzen
  useEffect(() => {
    if (!selected) return;
    if (htmlMailDisplay === 'html' && selected.body_html) setContentView('html');
    else setContentView('plain');
  }, [selected?.id, htmlMailDisplay]);

  // Auswahl bei Seitenwechsel / Filterwechsel zurücksetzen
  useEffect(() => setSelectedIds(new Set()), [page, filters]);

  // Breakpoint beobachten (für Listen-/Detail-Umschaltung auf dem Handy)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Direktlink von außen (z.B. Dashboard): ?open=<mail-id> öffnet die Mail sofort
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    mailApi.get(openId).then(({ data }) => setSelected(data)).catch(() => {});
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [searchParams]);

  const onDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (moveEvent) => {
      if (!isDragging.current) return;
      const delta = dragStartX.current - moveEvent.clientX;
      const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + delta));
      setPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setPanelWidth(prev => {
        localStorage.setItem('mail_panel_width', String(prev));
        return prev;
      });
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth]);

  const handleFetchNow = async () => {
    setFetching(true);
    try {
      const { data } = await mailAccountApi.list();
      const accs = data.results || data;
      const lastCheckedBefore = new Map(accs.map(a => [a.id, a.last_checked_at]));
      await Promise.all(accs.map(a => mailAccountApi.fetchNow(a.id)));

      // Statt einer festen Wartezeit (die bei mehreren/vollen Postfächern zu
      // kurz sein kann und dann veraltete Daten zeigt) auf last_checked_at
      // pollen — dieses Feld wird vom Celery-Task erst NACH vollständigem
      // Abruf + KI-Klassifizierung aller neuen Mails aktualisiert.
      const maxWaitMs = 30000;
      const intervalMs = 1500;
      const start = Date.now();
      while (Date.now() - start < maxWaitMs) {
        await new Promise(r => setTimeout(r, intervalMs));
        try {
          const { data: current } = await mailAccountApi.list();
          const currentAccs = current.results || current;
          const allDone = currentAccs.every(a => {
            const prev = lastCheckedBefore.get(a.id);
            return prev === undefined || (a.last_checked_at && a.last_checked_at !== prev);
          });
          if (allDone) break;
        } catch {
          break;
        }
      }
    } catch {
      // Abruf konnte nicht gestartet werden — trotzdem neu laden, falls sich
      // zwischenzeitlich (z.B. durch Push) etwas geändert hat.
    } finally {
      load();
      setFetching(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { page };
    if (filters.search) params.search = filters.search;
    if (filters.category) params.category = filters.category;
    if (filters.mail_account) params.mail_account = filters.mail_account;
    if (filters.is_read !== '') params.is_read = filters.is_read;
    if (filters.is_spam !== '') params.is_spam = filters.is_spam;
    if (filters.date_from) params.received_after = filters.date_from;
    if (filters.date_to) params.received_before = filters.date_to + 'T23:59:59';
    mailApi.list(params).then(({ data }) => {
      setMails(data.results || data);
      setTotal(data.count || (data.results || data).length);
    }).finally(() => setLoading(false));
  }, [filters, page]);

  useEffect(() => {
    categoryApi.list().then(({ data }) => setCategories(data.results || data));
    mailAccountApi.list().then(({ data }) => setAccounts(data.results || data));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Bei eingehendem Push bzw. Klick auf eine Benachrichtigung (siehe
  // PortalLayout.jsx / sw.js) die Mail-Liste automatisch neu laden, statt
  // dass der Nutzer manuell neu laden muss.
  useEffect(() => {
    const handleRefresh = () => load();
    window.addEventListener('incomail:refresh-mails', handleRefresh);
    return () => window.removeEventListener('incomail:refresh-mails', handleRefresh);
  }, [load]);

  const handleMarkRead = async (id) => {
    await mailApi.markRead(id);
    setMails(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
    if (selected?.id === id) setSelected(prev => ({ ...prev, is_read: true }));
  };

  const handleReclassify = async (id) => {
    await mailApi.reclassify(id);
    alert('Neuklassifizierung gestartet.');
  };

  const handleMarkSpam = async (id, isSpam) => {
    await mailApi.markSpam(id, isSpam);
    if (isSpam) {
      setMails(prev => prev.filter(m => m.id !== id));
      setSelected(null);
      setTotal(prev => prev - 1);
    } else {
      setMails(prev => prev.map(m => m.id === id ? { ...m, is_spam: false } : m));
      if (selected?.id === id) setSelected(prev => ({ ...prev, is_spam: false }));
    }
  };

  // ── Einzeln löschen ────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Mail aus der Datenbank löschen?\n\nDas Original auf dem Mailserver bleibt erhalten.')) return;
    await mailApi.delete(id);
    setMails(prev => prev.filter(m => m.id !== id));
    if (selected?.id === id) setSelected(null);
    setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    setTotal(prev => prev - 1);
  };

  // ── Bulk-Aktionen ───────────────────────────────────────────
  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!window.confirm(`${ids.length} Mail(s) aus der Datenbank löschen?\n\nDie Originale auf dem Mailserver bleiben erhalten.`)) return;
    await mailApi.bulkDelete(ids);
    setMails(prev => prev.filter(m => !selectedIds.has(m.id)));
    if (selectedIds.has(selected?.id)) setSelected(null);
    setTotal(prev => prev - ids.length);
    setSelectedIds(new Set());
  };

  const handleBulkSpam = async () => {
    const ids = [...selectedIds];
    await mailApi.bulkSpam(ids, true);
    setMails(prev => prev.filter(m => !selectedIds.has(m.id)));
    if (selectedIds.has(selected?.id)) setSelected(null);
    setTotal(prev => prev - ids.length);
    setSelectedIds(new Set());
  };

  // ── Checkbox-Helfer ─────────────────────────────────────────
  const allSelected = mails.length > 0 && mails.every(m => selectedIds.has(m.id));
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(mails.map(m => m.id)));
  };
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full">
      {compose !== null && (
        <ComposeModal replyTo={compose || null} onClose={() => setCompose(null)} />
      )}
      {forward !== null && (
        <ComposeModal forwardOf={forward} onClose={() => setForward(null)} />
      )}

      {/* Mailliste (auf dem Handy ausgeblendet, solange eine Mail geöffnet ist) */}
      <div className={`flex-1 flex-col overflow-hidden min-w-0 ${selected && !isDesktop ? 'hidden' : 'flex'}`}>
        <div className="p-4 bg-white border-b border-gray-200 flex flex-col gap-2">
          {/* Zeile 1: Suche (volle Breite auf dem Handy) + Aktionen */}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text" placeholder="Suchen (Betreff, Absender...)"
              value={filters.search}
              onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
              className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <div className="flex gap-2">
              <button onClick={handleFetchNow} disabled={fetching}
                className="flex-1 sm:flex-none px-4 py-2 border border-green-300 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 whitespace-nowrap disabled:opacity-50">
                {fetching ? '⏳ Abruf...' : '↻ Abrufen'}
              </button>
              <button onClick={() => setCompose(false)}
                className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 whitespace-nowrap">
                ✉ Neue Mail
              </button>
            </div>
          </div>
          {/* Zeile 2: Filter (auf dem Handy jeweils volle Breite, gleich breit) */}
          <div className="flex flex-col sm:flex-row gap-2 sm:flex-wrap">
            <select value={filters.category} onChange={e => { setFilters(f => ({ ...f, category: e.target.value })); setPage(1); }}
              className="w-full sm:w-auto px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">Alle Kategorien</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filters.mail_account} onChange={e => { setFilters(f => ({ ...f, mail_account: e.target.value })); setPage(1); }}
              className="w-full sm:w-auto px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">Alle Konten</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.email_address}</option>)}
            </select>
            <select value={filters.is_read} onChange={e => { setFilters(f => ({ ...f, is_read: e.target.value })); setPage(1); }}
              className="w-full sm:w-auto px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">Gelesen/Ungelesen</option>
              <option value="false">Ungelesen</option>
              <option value="true">Gelesen</option>
            </select>
            <div className="w-full sm:w-auto flex items-center gap-1.5 text-sm text-gray-500">
              <span>Von</span>
              <input type="date" value={filters.date_from}
                onChange={e => { setFilters(f => ({ ...f, date_from: e.target.value })); setPage(1); }}
                className="flex-1 sm:flex-none px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              <span>Bis</span>
              <input type="date" value={filters.date_to}
                onChange={e => { setFilters(f => ({ ...f, date_to: e.target.value })); setPage(1); }}
                className="flex-1 sm:flex-none px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
            {(filters.category || filters.mail_account || filters.is_read || filters.date_from || filters.date_to) && (
              <button onClick={() => { setFilters(f => ({ ...f, category: '', mail_account: '', is_read: '', date_from: '', date_to: '' })); setPage(1); }}
                className="w-full sm:w-auto px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
                ✕ Filter zurücksetzen
              </button>
            )}
          </div>
        </div>

        {/* Bulk-Aktionsleiste */}
        {selectedIds.size > 0 && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-3 text-sm">
            <span className="text-blue-800 font-medium">{selectedIds.size} Mail{selectedIds.size !== 1 ? 's' : ''} ausgewählt</span>
            <button onClick={() => setSelectedIds(new Set())}
              className="text-blue-600 hover:underline text-xs">
              Alle abwählen
            </button>
            <div className="flex-1" />
            <button onClick={handleBulkSpam}
              className="px-3 py-1.5 border border-red-300 text-red-700 rounded-lg text-xs hover:bg-red-50 font-medium">
              🚫 Als Spam markieren
            </button>
            <button onClick={handleBulkDelete}
              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700 font-medium">
              🗑 Löschen (aus DB)
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading ? <LoadingSpinner /> : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs sticky top-0">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      title="Alle auf dieser Seite auswählen"
                    />
                  </th>
                  {['Absender', 'Betreff', 'Kategorie', 'Conf.', 'Konto', 'Datum', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {mails.map(m => (
                  <tr key={m.id}
                    onClick={() => setSelected(m)}
                    className={`cursor-pointer hover:bg-blue-50 ${selected?.id === m.id ? 'bg-blue-50' : ''} ${selectedIds.has(m.id) ? 'bg-blue-50' : ''} ${!m.is_read ? 'font-semibold' : ''}`}>
                    <td className="px-3 py-3 w-10" onClick={e => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selectedIds.has(m.id)}
                        onChange={() => toggleOne(m.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-xs max-w-[130px] truncate">{m.sender}</td>
                    <td className="px-4 py-3 max-w-[200px] truncate">
                      {!m.is_read && <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-2" />}
                      {m.subject || '(kein Betreff)'}
                    </td>
                    <td className="px-4 py-3">
                      {m.category_name && (
                        <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{m.category_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><ConfidenceBadge value={m.confidence} /></td>
                    <td className="px-4 py-3 text-xs text-gray-400 max-w-[160px] truncate" title={m.mail_account_email}>
                      {m.mail_account_email || '–'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {m.received_at ? new Date(m.received_at).toLocaleString('de-DE', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '–'}
                    </td>
                    <td className="px-4 py-3">
                      {m.has_attachments && <span title="Anhang" className="text-gray-400">📎</span>}
                    </td>
                  </tr>
                ))}
                {mails.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">Keine E-Mails gefunden.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 py-3 bg-white border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
          <span>{total} E-Mail{total !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">
              ← Zurück
            </button>
            <span className="px-3 py-1">Seite {page}</span>
            <button disabled={mails.length < 50} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">
              Weiter →
            </button>
          </div>
        </div>
      </div>

      {/* Ziehbarer Trenner (nur auf größeren Bildschirmen, auf dem Handy wechselt die Ansicht komplett) */}
      {selected && isDesktop && (
        <div
          onMouseDown={onDividerMouseDown}
          style={{ cursor: 'col-resize' }}
          className="w-1.5 flex-shrink-0 bg-gray-200 hover:bg-blue-400 transition-colors relative group select-none"
          title="Ziehen zum Anpassen der Breite"
        >
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center pointer-events-none">
            <div className="w-0.5 h-10 bg-gray-400 group-hover:bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      )}

      {/* Detailansicht: auf dem Handy volle Breite statt Seitenpanel */}
      {selected && (
        <div
          className={`flex-shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden ${isDesktop ? '' : 'w-full'}`}
          style={isDesktop ? { width: panelWidth } : undefined}
        >
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              {!isDesktop && (
                <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-700 flex-shrink-0" title="Zurück zur Liste">←</button>
              )}
              <h3 className="font-semibold text-sm text-gray-900 truncate">{selected.subject || '(kein Betreff)'}</h3>
            </div>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">✕</button>
          </div>
          <div className="p-4 space-y-3 text-sm flex-1 overflow-auto">
            <div><span className="text-gray-500">Von:</span> <span className="ml-1 break-all">{selected.sender}</span></div>
            <div><span className="text-gray-500">An:</span> <span className="ml-1 break-all">{selected.recipient || '–'}</span></div>
            <div><span className="text-gray-500">Datum:</span> <span className="ml-1">{selected.received_at ? new Date(selected.received_at).toLocaleString('de-DE') : '–'}</span></div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-500">Kategorie:</span>
              <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{selected.category_name || '–'}</span>
              <ConfidenceBadge value={selected.confidence} />
            </div>
            {selected.classification_reason && (
              <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">{selected.classification_reason}</div>
            )}

            {selected.attachments && selected.attachments.length > 0 && (
              <div className="border-t border-gray-100 pt-3">
                <div className="text-xs text-gray-500 mb-2">📎 {selected.attachments.length} Anhang{selected.attachments.length !== 1 ? 'hänge' : ''}</div>
                <div className="space-y-1">
                  {selected.attachments.map(att => (
                    <div key={att.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800 truncate">{att.filename}</div>
                        <div className="text-xs text-gray-400">{att.mime_type} · {att.size_bytes > 1024*1024 ? (att.size_bytes/1024/1024).toFixed(1)+' MB' : Math.round(att.size_bytes/1024)+' KB'}</div>
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const { data } = await attachmentApi.download(att.id);
                            const url = URL.createObjectURL(data);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = att.filename;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch {
                            alert('Download fehlgeschlagen.');
                          }
                        }}
                        className="ml-2 text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
                      >
                        ↓ Laden
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-gray-500">Inhalt:</div>
                {htmlMailDisplay !== 'text' && selected.body_html && (
                  <button
                    onClick={() => setContentView(v => v === 'html' ? 'plain' : 'html')}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {contentView === 'html' ? 'Als Text anzeigen' : 'Formatiert anzeigen (HTML)'}
                  </button>
                )}
              </div>
              {contentView === 'html' && selected.body_html ? (
                <div className="border border-gray-200 rounded overflow-hidden max-h-[600px] overflow-y-auto">
                  <SafeHtmlMail html={selected.body_html} />
                </div>
              ) : (
                <div className="text-sm whitespace-pre-wrap text-gray-700 bg-gray-50 rounded p-3 max-h-96 overflow-auto leading-relaxed">
                  {selected.body_plain || selected.body_preview || '(kein Inhalt)'}
                </div>
              )}
              {selected.copy_complete && selected.content_expires_at && (
                <div className="text-xs text-gray-400 mt-1">
                  Kopie gespeichert · Inhalt verfällt am {new Date(selected.content_expires_at).toLocaleDateString('de-DE')}
                </div>
              )}
            </div>
          </div>
          <div className="p-4 border-t border-gray-100 flex flex-col gap-2">
            <div className="flex gap-2">
              <button onClick={() => setCompose(selected)}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700">
                ↩ Antworten
              </button>
              <button onClick={() => setForward(selected)}
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700">
                ↗ Weiterleiten
              </button>
            </div>
            <div className="flex gap-2">
              {!selected.is_read && (
                <button onClick={() => handleMarkRead(selected.id)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-xs hover:bg-gray-50">
                  Als gelesen markieren
                </button>
              )}
              <button onClick={() => handleReclassify(selected.id)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-xs hover:bg-gray-50">
                Neu klassifizieren
              </button>
              {selected.is_spam ? (
                <button onClick={() => handleMarkSpam(selected.id, false)}
                  className="flex-1 py-2 border border-green-300 text-green-700 rounded-lg text-xs hover:bg-green-50">
                  Kein Spam
                </button>
              ) : (
                <button onClick={() => handleMarkSpam(selected.id, true)}
                  className="flex-1 py-2 border border-red-300 text-red-700 rounded-lg text-xs hover:bg-red-50">
                  Als Spam
                </button>
              )}
            </div>
            <button onClick={() => handleDelete(selected.id)}
              className="w-full py-2 border border-red-200 text-red-600 rounded-lg text-xs hover:bg-red-50">
              🗑 Aus Datenbank löschen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
